import { BsLink } from "react-icons/bs";
import { useState, ChangeEvent } from "react";

import { DeleteIcon } from "../../assets/Icons";
import {
  AccountProps,
  LabelInputProps,
  InputDeleteProps,
  TitleSubtitleTypes,
  SelectProps,
} from "./types";

export const InternalContainer = ({ children, headerText }: AccountProps) => {
  return (
    <div className="col-6 rounded-lg p-6 border border-border-border1 dark:border-tabs bg-white dark:bg-BG-dark">
      <span className="satoshi-xl-bold-pry-900 text-gray-500 dark:text-white">
        {headerText}
      </span>
      {children}
    </div>
  );
};

export const ExternalContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <div className="px-4  pb-2 bp:p-0">{children}</div>;
};

export const LabelInput = ({
  value,
  placeholder,
  register,
  type,
  name,
  disabled,
  onChange,
  required,
}: LabelInputProps) => {
  const inputId = name;
  return (
    <div className="relative">
      <input
        id={inputId}
        type={type}
        {...(register && name ? register(name) : {})}
        name={name}
        {...(disabled ? { disabled: true } : {})}
        className="peer h-12 w-full border border-inputBorder p-3 dark:bg-white/10 rounded-lg satoshi-base-medium-black  placeholder-transparent focus:outline-none focus:border-black dark:text-white"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        {...(required ? { required: true } : {})}
      />
      <label
        htmlFor={inputId}
        className="absolute  left-3.5 -top-2.5 px-1 peer-focus:bg-white  peer-focus:dark:bg-inherit satoshi-sm-medium-black transition-all  peer-placeholder-shown:text-base  peer-placeholder-shown:text-blackpeer-placeholder-shown:top-3 peer-focus:-top-2.5 peer-focus:text-black dark:text-white peer-focus:text-sm"
      >
        {placeholder}
      </label>
    </div>
  );
};

export const DownloadLinkInput = ({
  value = "",
  name = "",
  onChange = (e: ChangeEvent<HTMLInputElement>) => {},
  placeholder = "Download Link",
  type = "text",
  disabled = false,
  iconType = "google", // 'google' or 'apple'
}) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="relative w-full mb-4">
      <div
        className={`flex items-center w-full rounded-xl border ${
          isFocused ? "border-gray-700" : "border-gray-300"
        } bg-white overflow-hidden`}
      >
        {/* Left Icon */}
        <div className="flex items-center justify-center pl-4">
          {iconType === "google" ? (
            <div className="w-6 h-6 flex items-center justify-center">
              <img
                src="/images/googleplay.png"
                alt="Google Play"
                className="w-[12] h-[12]"
              />
            </div>
          ) : (
            <div className="w-6 h-6 flex items-center justify-center">
              <img
                src="/images/apple.png"
                alt="Google Play"
                className="w-[12] h-[12]"
              />
            </div>
          )}
        </div>

        {/* Input */}
        <input
          id={name}
          type={type}
          name={name}
          disabled={disabled}
          className="flex-1 p-3 text-base font-medium text-gray-900 bg-transparent border-none focus:outline-none"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />

        {/* Right Icon */}
        <div className="flex items-center justify-center pr-4">
          <BsLink className="w-5 h-5 text-gray-800" />
        </div>
      </div>
    </div>
  );
};

export const LabelInputWithDelete = ({
  value,
  placeholder,
  type,
  name,
  disabled,
  onChange,
  onDelete,
}: InputDeleteProps) => {
  return (
    <div className="relative">
      <input
        id={value}
        type={type}
        name={name}
        {...(disabled ? { disabled: true } : {})}
        className="peer w-full border border-inputBorder p-3 dark:bg-white/10 rounded-lg satoshi-base-medium-black placeholder-transparent focus:outline-none focus:border-black pr-10" // Added pr-10 for padding
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      />
      <label
        htmlFor={value}
        className="absolute left-3.5 -top-2.5 px-1 peer-focus:bg-white peer-focus:dark:bg-inherit satoshi-sm-medium-black transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-black peer-placeholder-shown:top-3 peer-focus:-top-2.5 peer-focus:text-black peer-focus:text-sm"
      >
        {placeholder}
      </label>

      {/* Delete icon - only shown if onDelete is provided and there's a value */}

      <button
        type="button"
        onClick={onDelete}
        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
        aria-label="Clear input"
      >
        <DeleteIcon />
      </button>
    </div>
  );
};

export const TitleSubtitle = ({ title, subtitle }: TitleSubtitleTypes) => {
  return (
    <div className="flex flex-col gap-[2px]">
      <span className="lt-base-semibold-toggleBG-dark dark:text-white">
        {title}
      </span>
      <span className="lt-sm-normal-tabs dark:text-main-dark">{subtitle}</span>
    </div>
  );
};

export const FloatingLabelSelect: React.FC<SelectProps> = ({
  value = "",
  placeholder,
  name,
  disabled = false,
  onChange,
  options,
}) => {
  return (
    <div className="w-full">
      <label
        htmlFor={name}
        className="block text-gray-700 dark:text-white mb-2 satoshi-sm-medium-black"
      >
        {placeholder}
      </label>
      <div className="relative">
        <select
          id={name}
          name={name}
          disabled={disabled}
          value={value}
          onChange={onChange}
          className="w-full border border-inputBorder p-3 dark:bg-white/10 dark:text-white rounded-lg satoshi-base-medium-black appearance-none focus:outline-none focus:border-gray-400 transition-colors pr-10"
        >
          <option value="" disabled className="dark:bg-black dark:text-white">
            Select {placeholder}
          </option>
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              className="dark:bg-black dark:text-white"
            >
              {option.label}
            </option>
          ))}
        </select>
        {/* Dropdown arrow icon */}
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <svg
            className="w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};
